"use client";

import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { useRouter } from "next/navigation";
import { clearSession, getStoredUser, getToken, logoutEverywhere } from "./api";
import { clearFetchCache } from "./use-fetch";
import type { AuthUser } from "./types";

interface AuthState {
  user: AuthUser | null;
  loading: boolean;
  logout: () => void;
}

const AuthContext = createContext<AuthState>({ user: null, loading: true, logout: () => {} });

export function AuthProvider({ children }: { children: ReactNode }) {
  const router = useRouter();
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = getToken();
    const stored = getStoredUser<AuthUser>();
    if (!token || !stored) {
      router.replace("/login");
      return;
    }
    // Role gate: the customer portal is for USER accounts only. Reject any other role
    // (e.g. an admin session) so it can't linger here.
    if (stored.role !== "USER") {
      clearSession();
      router.replace("/login");
      return;
    }
    setUser(stored);
    setLoading(false);
  }, [router]);

  const logout = async () => {
    await logoutEverywhere();  // revoke server-side so every connected app signs out too
    clearFetchCache();   // never let the next sign-in see the previous account's cached data
    router.replace("/");  // land on the public marketing page, not the sign-in form
  };

  return (
    <AuthContext.Provider value={{ user, loading, logout }}>{children}</AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
